/**
 * PageState — loading / error / empty / access-denied body states.
 *
 * Rendered INSIDE PageContainer so the PageHeader stays intact — pages must
 * never downgrade or drop their title for these states (the old pattern of
 * swapping the h1 for a bare <h2 className="font-semibold"> is what this
 * replaces).
 */
import React from 'react'
import { Loader2, AlertTriangle, Inbox, Lock } from 'lucide-react'

type PageStateVariant = 'loading' | 'error' | 'empty' | 'denied'

const defaultIcons: Record<PageStateVariant, React.ReactNode> = {
  loading: <Loader2 className="h-6 w-6 animate-spin text-ink-400" />,
  error: <AlertTriangle className="h-6 w-6 text-attention" />,
  empty: <Inbox className="h-6 w-6 text-ink-300" />,
  denied: <Lock className="h-6 w-6 text-ink-300" />,
}

interface PageStateProps {
  variant: PageStateVariant
  title?: React.ReactNode
  message?: React.ReactNode
  /** Optional action (e.g. Retry button, "New item" button). */
  action?: React.ReactNode
  icon?: React.ReactNode
  className?: string
}

export function PageState({ variant, title, message, action, icon, className = '' }: PageStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center rounded-[10px] border border-v2border bg-surface px-6 py-16 text-center shadow-v2-1 ${className}`}
      role={variant === 'error' ? 'alert' : undefined}
      aria-busy={variant === 'loading' || undefined}
    >
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-paper-2">
        {icon ?? defaultIcons[variant]}
      </div>
      {title !== undefined && (
        <div className="font-display text-sm font-semibold tracking-[-0.01em] text-ink">
          {title}
        </div>
      )}
      {message !== undefined && (
        <p className="mt-1 max-w-md text-[13px] text-ink-500">{message}</p>
      )}
      {action && <div className="mt-4 flex items-center gap-2">{action}</div>}
    </div>
  )
}
